// src/components/usuario/ContenidoMultimedia.jsx
import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import axios from "axios";
import backgroundImage from "../../assets/img/fondoSeis.png";
import logoCIE from "../../assets/img/logoCIE.svg";
import useLogout from "../../hooks/useLogout";
import HeaderUsuario from "../header/HeaderUsuario";

const API_BASE_URL = process.env.REACT_APP_API_URL || "";

const ContenidoMultimedia = () => {
  const { temaId } = useParams();
  const navigate = useNavigate();
  const logout = useLogout();

  // Ahora manejamos múltiples contenidos
  const [contenidos, setContenidos] = useState([]); // [{slot, url, tipo}]
  const [selectedSlot, setSelectedSlot] = useState(1);
  const [error, setError] = useState("");

  useEffect(() => {
    cargarContenidoMultimedia();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [temaId]);

  useEffect(() => {
    const slots = contenidos.map((c) => c.slot).sort((a, b) => a - b);
    if (slots.length > 0 && !slots.includes(selectedSlot)) {
      setSelectedSlot(slots[0]);
    }
  }, [contenidos, selectedSlot]);

  const detectarTipo = (url) => {
    const clean = (url || "").split("?")[0];
    const ext = (clean.split(".").pop() || "").toLowerCase();
    const typeMap = {
      image: ["jpg", "jpeg", "png", "gif", "webp"],
      video: ["mp4", "webm", "mov", "m4v", "avi"],
      pdf: ["pdf"],
    };
    if (typeMap.image.includes(ext)) return "image";
    if (typeMap.video.includes(ext)) return "video";
    if (typeMap.pdf.includes(ext)) return "pdf";
    return "url"; // fallback
  };

  const cacheBust = (url = "") =>
    url ? `${url}${url.includes("?") ? "&" : "?"}t=${Date.now()}` : url;

  const cargarContenidoMultimedia = async () => {
    const token = localStorage.getItem("token");

    // Si no hay token, no intentes llamar: cierra sesión y sal
    if (!token) {
      setError("Tu sesión no está activa. Inicia sesión nuevamente.");
      handleLogout(); // ya lo tienes definido abajo
      return;
    }

    try {
      const { data } = await axios.get(
        `${API_BASE_URL}/api/temas/contenido-multimedia/${temaId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let arr = [];
      if (Array.isArray(data?.contenidos)) {
        arr = data.contenidos;
      } else if (data?.url) {
        arr = [{ slot: 1, url: data.url, tipo: detectarTipo(data.url) }];
      }

      // Normaliza y ordena
      const normalized = arr
        .map((c) => ({
          slot: Number(c.slot),
          url: c.url,
          tipo: c.tipo || detectarTipo(c.url),
        }))
        .sort((a, b) => a.slot - b.slot);

      setContenidos(normalized);

      // Asegura que el slot seleccionado exista; si no, usa el primero
      const firstSlot = normalized[0]?.slot ?? 1;
      setSelectedSlot((prev) =>
        normalized.some((c) => c.slot === prev) ? prev : firstSlot
      );

      setError("");
    } catch (e) {
      console.error("Error al cargar contenido multimedia:", e);
      const status = e?.response?.status;

      if (status === 401 || status === 403) {
        // Sesión expirada o sin permisos -> cierra sesión y redirige
        setError(
          status === 401
            ? "Tu sesión expiró. Por favor, inicia sesión nuevamente."
            : "No tienes permisos para ver este contenido."
        );
        handleLogout(); // limpia token y navega a login
        return;
      }

      setContenidos([]);
      setSelectedSlot(1);
      setError("No fue posible cargar el contenido multimedia.");
    }
  };

  const handleContinuar = () => {
    navigate(`/tema/quiz/${temaId}`);
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const mimeByExt = {
    mp4: "video/mp4",
    webm: "video/webm",
    mov: "video/quicktime",
    m4v: "video/x-m4v",
    avi: "video/x-msvideo",
  };

  const renderContenido = (item) => {
    if (!item?.url) {
      return <p>No hay contenido multimedia disponible para este tema.</p>;
    }

    // Aplica cache-bust SOLO al seleccionado y usa como key para forzar remount
    const bustedUrl = cacheBust(item.url);

    if (item.tipo === "image") {
      return (
        <img
          key={`${selectedSlot}-${bustedUrl}`}
          src={bustedUrl}
          alt="Contenido del tema"
          className="w-full mb-4 rounded"
        />
      );
    }

    if (item.tipo === "video") {
      const clean = item.url.split("?")[0];
      const ext = (clean.split(".").pop() || "").toLowerCase();
      return (
        <video
          key={`${selectedSlot}-${bustedUrl}`}
          controls
          className="w-full mb-4 rounded"
        >
          <source src={bustedUrl} type={mimeByExt[ext] || "video/mp4"} />
          Tu navegador no soporta el elemento de video.
        </video>
      );
    }

    if (item.tipo === "pdf") {
      return (
        <div className="w-full mb-4">
          <iframe
            key={`${selectedSlot}-${bustedUrl}`}
            src={bustedUrl}
            title="Documento PDF"
            className="w-full"
            style={{ height: "75vh", border: "none" }}
          />
          <div className="mt-2 text-center">
            <a
              href={bustedUrl}
              target="_blank"
              rel="noreferrer"
              className="underline"
            >
              Abrir PDF en nueva pestaña
            </a>
          </div>
        </div>
      );
    }

    // Fallback: enlace
    return (
      <div className="w-full mb-4 text-center">
        <a
          href={bustedUrl}
          target="_blank"
          rel="noreferrer"
          className="underline"
        >
          Abrir contenido
        </a>
      </div>
    );
  };

  const selectedItem = contenidos.find((c) => c.slot === selectedSlot);

  return (
    <div
      className="min-h-screen flex flex-col font-montserrat relative"
      style={{
        backgroundImage: `url(${backgroundImage})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      {/* Usuario / logout */}
      <div className="absolute top-4 right-4 z-10">
        <HeaderUsuario onLogout={handleLogout} />
      </div>

      {/* Volver */}
      <Link to="/niveles">
        <div className="absolute top-4 left-4 z-10 bg-yellow-500 text-white p-2 rounded">
          <button className="bg-transparent border-none cursor-pointer font-montserrat font-bold">
            Volver
          </button>
        </div>
      </Link>

      {/* Contenido */}
      <div className="flex-grow flex justify-center items-center p-8">
        <div className="max-w-3xl w-full">
          <h2 className="text-2xl font-bold mb-6 text-center">
            <span className="bg-white bg-opacity-80 rounded px-4 py-2 inline-block">
              Material de apoyo
            </span>
          </h2>

          {/* Tabs/píldoras para contenidos existentes */}
          {contenidos.length > 1 && (
            <div className="flex justify-center gap-2 mb-6">
              {Array.from(new Set(contenidos.map((c) => c.slot)))
                .sort((a, b) => a - b)
                .map((slot) => (
                  <button
                    key={slot}
                    onClick={() => setSelectedSlot(slot)}
                    className={`px-4 py-2 rounded-full border transition ${
                      selectedSlot === slot
                        ? "bg-green-700 text-white border-green-700"
                        : "bg-white text-green-700 border-green-700 hover:bg-green-50"
                    }`}
                    aria-label={`Ver contenido ${slot}`}
                    title={`Ver contenido ${slot}`}
                  >
                    {`Contenido ${slot}`}
                  </button>
                ))}
            </div>
          )}

          <div className="mb-8 bg-white bg-opacity-80 rounded p-4">
            {error ? (
              <p className="text-center text-red-600">{error}</p>
            ) : contenidos.length === 0 ? (
              <p className="text-center">
                No hay contenido multimedia disponible.
              </p>
            ) : (
              renderContenido(selectedItem || contenidos[0])
            )}

            <div className="flex justify-center mt-8">
              <button
                onClick={handleContinuar}
                className="bg-green-600 text-white font-bold py-2 px-6 rounded hover:bg-green-700"
              >
                Continuar
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Logo CIE */}
      <div className="absolute bottom-4 right-5 z-10">
        <img src={logoCIE} alt="Logo CIE" className="h-10 w-auto" />
      </div>
    </div>
  );
};

export default ContenidoMultimedia;
